import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { CancelSubmitButtons } from "~/common/components";
import Modal, { ModalProps } from "~/common/components/Modal";
import { QUESTION_TYPES } from "~/common/utils";
import {
  QUESTION_CHOICE_SELECTION_METHOD,
  QUESTION_TYPE,
} from "~/providers/constants/supplementalQuestions";
import {
  useGetCurrentOrganizationId,
  useUpdateSupplementalQuestion,
} from "~/providers/hooks";
import { editQuestionFormSchema } from "~/providers/schemas/supplementalQuestions";
import {
  AddQuestionForm,
  EditQuestionForm,
  SupplementalQuestion,
} from "~/providers/types/supplementalQuestions";
import { mapInitialDataToFormDefaults } from "~/providers/utils/supplementalQuestions";
import { useSupplementalQuestionsEditorContext } from "./SupplementalQuestionsEditor";
import { SupplementalQuestionUpsertForm } from "./SupplementalQuestionUpsertForm";

interface EditSupplementalQuestionModalProps extends ModalProps {
  initialData: SupplementalQuestion;
}

export const EditSupplementalQuestionModal = ({
  initialData,
  onClose,
  ...props
}: EditSupplementalQuestionModalProps) => {
  const { type: consultationType } = useSupplementalQuestionsEditorContext();
  const organizationId = useGetCurrentOrganizationId();
  const updateSupplementalQuestionMutation = useUpdateSupplementalQuestion();

  const form = useForm<EditQuestionForm>({
    resolver: zodResolver(editQuestionFormSchema),
    mode: "onChange",
    defaultValues: mapInitialDataToFormDefaults(initialData),
  });

  const onSubmit = (data: AddQuestionForm) => {
    const type =
      data.type === QUESTION_TYPE.MULTIPLE_CHOICE
        ? data.selectionMethod === QUESTION_CHOICE_SELECTION_METHOD.MULTIPLE
          ? QUESTION_TYPES.supplemental_multi_select
          : QUESTION_TYPES.supplemental_single_select
        : data.type === QUESTION_TYPE.YES_NO
          ? QUESTION_TYPES.supplemental_single_select
          : QUESTION_TYPES.supplemental_free_text;

    const choices =
      data.type === QUESTION_TYPE.MULTIPLE_CHOICE
        ? (data.choices.map((choice) => choice.label) ?? [])
        : data.type === QUESTION_TYPE.YES_NO
          ? ["yes", "no"]
          : [];

    if (!organizationId) return;

    updateSupplementalQuestionMutation.mutate(
      {
        consultationType,
        organizationId,
        id: initialData.id,
        text: data.text,
        type,
        choices,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      title="Edit question"
      className={twMerge("max-h-[80vh] overflow-scroll xl:w-1/2")}
      onClose={onClose}
      {...props}
    >
      <FormProvider {...form}>
        <form
          className="flex flex-col gap-6"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <SupplementalQuestionUpsertForm isEdit={true} />

          <CancelSubmitButtons
            submitButtonLabel="Save changes"
            handleCancel={onClose}
            isSubmitting={updateSupplementalQuestionMutation.isPending}
            containerClassName="w-fit ml-auto"
            disableSubmit={!form.formState.isValid}
          />
        </form>
      </FormProvider>
    </Modal>
  );
};
